import styled from "@emotion/styled"
import { PButton, PText } from "@porsche-design-system/components-react"
import { spacingStaticSm } from "@porsche-design-system/components-react/emotion"
import { useCallback, useState } from "react"
import { defineMessages, FormattedMessage, useIntl } from "react-intl"
import { AiGeneratedContentNotification } from "src/components/aiGeneratedContentNotification/AiGeneratedContentNotification"
import { FormLayout } from "src/components/form/formLayout/FormLayout"
import { LocalizedTextAreaField } from "src/components/form/localizedTextAreaField/LocalizedTextAreaField"
import type { ListingDescriptionFields } from "./listingDescription.fields"

const messages = defineMessages({
	aiButtonLabel: {
		defaultMessage: "Generate AI description",
		id: "listingDescription.aiButtonLabel",
	},
	aiIntro: {
		defaultMessage:
			"Create a detailed description of the vehicle or save time with an AI-generated vehicle description. Visible to customer.",
		id: "listingDescription.aiIntro",
	},
	aiWarning: {
		defaultMessage: "The AI can make mistakes. Please check the text and adapt it individually if necessary.",
		id: "listingDescription.aiWarning",
	},
})

interface ListingDescriptionFormProps {
	fields: ListingDescriptionFields
	onGenerateAiListingDescriptionClick: (() => Promise<void>) | undefined
}

export const ListingDescriptionForm = ({
	fields,
	onGenerateAiListingDescriptionClick,
}: ListingDescriptionFormProps) => {
	const [isAiGenerating, setIsAiGenerating] = useState(false)
	const [isAiDisclaimerVisible, setIsAiDisclaimerVisible] = useState(false)
	const { formatMessage } = useIntl()

	const handleGenerateAiListingDescriptionClick = useCallback(() => {
		if (!onGenerateAiListingDescriptionClick) {
			return
		}
		setIsAiDisclaimerVisible(true)
		setIsAiGenerating(true)
		onGenerateAiListingDescriptionClick().finally(() => setIsAiGenerating(false))
	}, [onGenerateAiListingDescriptionClick])

	return (
		<FormLayout $width="half">
			<LocalizedTextAreaField
				{...fields.listingDescription}
				{...(onGenerateAiListingDescriptionClick && { descriptionText: formatMessage(messages.aiIntro) })}
				locales="customer-app"
			>
				{onGenerateAiListingDescriptionClick && (
					<AiButtonWrapper>
						<PButton
							type="button"
							variant="secondary"
							compact
							loading={isAiGenerating}
							onClick={handleGenerateAiListingDescriptionClick}
						>
							<FormattedMessage {...messages.aiButtonLabel} />
						</PButton>

						{isAiDisclaimerVisible && (
							<PText color="contrast-medium" size="xs">
								<FormattedMessage {...messages.aiWarning} />
							</PText>
						)}
					</AiButtonWrapper>
				)}
			</LocalizedTextAreaField>

			<AiGeneratedContentNotification />
		</FormLayout>
	)
}

const AiButtonWrapper = styled.div`
	display: flex;
	flex-direction: column;
	gap: ${spacingStaticSm};
	align-items: start;
	margin: ${spacingStaticSm} 0;
`
